import api from "./api";

export interface OrderItem {
  product: {
    _id: string;
    title: string;
    price: number;
    image: string;
    type: "físico" | "digital";
  };
  quantity: number;
  price: number;
}

export interface Order {
  _id: string;
  orderNumber: string;
  user: string;
  items: OrderItem[];
  totalItems: number;
  subtotal: number;
  shipping: number;
  total: number;
  status: "pending" | "processing" | "shipped" | "delivered" | "cancelled";
  shippingInfo: {
    fullName: string;
    address: string;
    city: string;
    postalCode: string;
    phone: string;
  };
  paymentMethod: string;
  paymentStatus: "pending" | "processing" | "completed" | "failed" | "refunded";
  paymentInfo?: {
    status?: "pending" | "processing" | "completed" | "failed" | "refunded";
    transactionId?: string;
    paymentMethod?: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface OrdersResponse {
  orders: Order[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalOrders: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

export interface OrderFilters {
  status?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

export interface DigitalDownload {
  productId: string;
  productTitle: string;
  downloadUrl: string;
  expiresAt: string;
  downloadCount: number;
  maxDownloads: number;
}

class OrderService {
  async getMyOrders(filters?: OrderFilters): Promise<OrdersResponse> {
    const params = new URLSearchParams();

    if (filters?.status) params.append("status", filters.status);
    if (filters?.startDate) params.append("startDate", filters.startDate);
    if (filters?.endDate) params.append("endDate", filters.endDate);
    if (filters?.page) params.append("page", filters.page.toString());
    if (filters?.limit) params.append("limit", filters.limit.toString());

    const response = await api.get(`/orders?${params.toString()}`);
    return {
      orders: response.data.data,
      pagination: response.data.pagination,
    };
  }

  async getOrderById(orderId: string): Promise<Order> {
    const response = await api.get(`/orders/${orderId}`);
    return response.data.data;
  }

  async downloadReceipt(
    orderId: string,
    options?: { guestEmail?: string }
  ): Promise<void> {
    const url = options?.guestEmail
      ? `/checkout/guest/receipt/${orderId}?email=${encodeURIComponent(
          options.guestEmail
        )}`
      : `/orders/${orderId}/receipt`;

    const response = await api.get(url, { responseType: "blob" });

    const disposition = response.headers["content-disposition"] as
      | string
      | undefined;
    const match = disposition?.match(/filename="?([^"';]+)"?/);
    const filename = match?.[1] || `recibo-${orderId}.pdf`;

    const blob = new Blob([response.data], { type: "application/pdf" });
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(objectUrl);
  }

  async downloadDigitalProduct(
    orderId: string,
    productId: string
  ): Promise<DigitalDownload> {
    const response = await api.get(`/orders/${orderId}/download/${productId}`);
    return response.data.data;
  }

  // Métodos utilitarios
  getStatusColor(status: Order["status"]): string {
    const colors = {
      pending: "bg-yellow-100 text-yellow-800",
      processing: "bg-blue-100 text-blue-800",
      shipped: "bg-purple-100 text-purple-800",
      delivered: "bg-green-100 text-green-800",
      cancelled: "bg-red-100 text-red-800",
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  }

  getStatusLabel(status: Order["status"]): string {
    const labels = {
      pending: "Pendiente",
      processing: "Procesando",
      shipped: "Enviado",
      delivered: "Entregado",
      cancelled: "Cancelado",
    };
    return labels[status] || status;
  }

  formatOrderDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString("es-ES", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }
}

export default new OrderService();
